import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation, Trans } from 'next-i18next';
import * as Yup from 'yup';
import { MixedSchema } from 'yup/lib/mixed';
import { RequiredBooleanSchema } from 'yup/lib/boolean';
import { AnyObject } from 'yup/lib/types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import Control, { CONTROL_TYPE } from '@components/Form/Control';
import { Agreements, FilesRow, Form, Title } from '@components/ApplyModal/styles';
import Button, { BUTTON_TYPE } from '@components/Button';
import FileUploadBox from '@components/ApplyModal/FileUploadBox';
import { API, APIRoutes } from '@utils/api';
import { apiValidationHandler } from '@utils/formHelpers';
import Preloader from '@components/Preloader';
import SuccessSubmission from '@components/SuccessSubmission';

interface ApplyModalProps {
  offer: Offer;
  cloaseHandler: () => void;
  setApplied: (state: boolean) => void;
}

type FormFields = {
  cv?: MixedSchema;
  letter?: MixedSchema;
  agreementPersonalData: RequiredBooleanSchema<boolean, AnyObject>;
  agreementVsibleForEmployer: RequiredBooleanSchema<boolean, AnyObject>;
  agreementRodo: RequiredBooleanSchema<boolean, AnyObject>;
};

type FileFieldError = {
  message?: string;
};

function ApplyModal({ offer, cloaseHandler, setApplied }: ApplyModalProps) {
  const [cv, setCv] = useState(null);
  const [success, setSuccess] = useState(false);
  const [letter, setLetter] = useState(null);
  const [cvError, setCvError] = useState<FileFieldError | null>(null);
  const [letterError, setLetterError] = useState<FileFieldError | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { t: offersTrans } = useTranslation('offers');
  const { t } = useTranslation('common');
  const { name } = offer;

  useEffect(() => {
    return () => {
      setApplied(success);
    };
  }, [setApplied, success]);

  const { mutate: applyToOffer, isLoading } = useMutation((data: FormData) =>
    API.put(APIRoutes.offerApply(offer.id), data)
  );

  const validationSchema = Yup.object().shape({
    application: Yup.lazy(() => {
      const fields: FormFields = {
        agreementPersonalData: Yup.boolean().required(t('Acceptance is required')),
        agreementVsibleForEmployer: Yup.boolean().required(t('Acceptance is required')),
        agreementRodo: Yup.boolean().required(t('Acceptance is required')),
      };

      if (offer.requireCv) {
        fields.cv = Yup.mixed().test('required', t('File is required'), (value) => {
          return value && value.path;
        });
      }

      if (offer.requireLetter) {
        fields.letter = Yup.mixed().test('required', t('File is required'), (value) => {
          return value && value.path;
        });
      }

      return Yup.object().shape({
        ...fields,
      });
    }),
  });

  const formOptions = { resolver: yupResolver(validationSchema) };
  const { handleSubmit, formState, control, register, setValue, setError } = useForm(formOptions);

  const { errors } = formState;

  const cvDropHandler = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles.length) {
        setValue('application.cv', acceptedFiles[0]);
        setCv(acceptedFiles[0].name);
      }
      if (rejectedFiles.length && rejectedFiles[0].errors.length) {
        setCvError({ message: rejectedFiles[0].errors[0].message });
      }
    },
    [setValue]
  );

  const letterDropHandler = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles.length) {
        setValue('application.letter', acceptedFiles[0]);
        setLetter(acceptedFiles[0].name);
      }
      if (rejectedFiles.length && rejectedFiles[0].errors.length) {
        setLetterError({ message: rejectedFiles[0].errors[0].message });
      }
    },
    [setValue]
  );

  const onSubmit = (values: { application: FormFields }) => {
    try {
      const formData = new FormData();

      Object.keys(values.application).forEach((key) => {
        if (key === 'letter' || key === 'cv') {
          if (key === 'letter' && letter) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            formData.append(`application[${key}]`, values.application[key]);
          }
          if (key === 'cv' && cv) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            formData.append(`application[${key}]`, values.application[key]);
          }
        } else {
          formData.append(`application[${key}]`, values.application[key]);
        }
      });

      applyToOffer(formData, {
        onError: ({ response: { data } }) => {
          setSuccess(false);
          if (data.errors?.offerId) {
            toast.error(data.errors.offerId[0]);
          }
          apiValidationHandler(data.errors, setError, values);
        },
        onSuccess: () => {
          setSuccess(true);
          setShowConfirmation(true);
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.debug(error);
    }
  };

  if (showConfirmation) {
    return (
      <SuccessSubmission confirmClick={cloaseHandler} message={offersTrans('apply.success', { name: offer.name })} />
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      {isLoading ? <Preloader overlay /> : null}
      <Title>
        <Trans i18nKey="apply.welcome" t={offersTrans} name={name}>
          Aplikujesz na stanowisko <strong>{{ name }}</strong>
        </Trans>
      </Title>

      <FilesRow>
        <FileUploadBox
          label="CV"
          register={register}
          fieldName="application.cv"
          onDropCallback={cvDropHandler}
          error={errors.application?.cv || cvError}
          selectedFile={cv}
        />
        <FileUploadBox
          label={t('Cover Letter', { ns: 'offers' })}
          register={register}
          fieldName="application.letter"
          onDropCallback={letterDropHandler}
          error={errors.application?.letter || letterError}
          selectedFile={letter}
        />
      </FilesRow>

      <Agreements>
        <Controller
          control={control}
          name="application.agreementPersonalData"
          render={({ field: { name: fieldName, onChange, value } }) => (
            <Control
              onChange={onChange}
              value={value}
              name={fieldName}
              checked={value}
              error={errors.application?.agreementPersonalData}
              label={
                <Trans i18nKey="apply.agreementPersonalData" t={offersTrans}>
                  Wyrażam zgodę na przetwarzanie przez Prezydenta Miasta Łodzi jako administratora, moich danych
                  osobowych w zakresie: imienia, nazwiska, adresu poczty elektronicznej w celu założenia konta oraz
                  innych danych podanych z własnej inicjatywy, a zawartych między innymi w CV, liście motywacyjnym w
                  celu aplikowania na dostępne w serwisie www.profi.lodz.pl oferty pracy. Wiem, że podanie danych jest
                  dobrowolne oraz, że mam prawo kontroli przetwarzania swoich danych osobowych, prawo dostępu do treści
                  tych danych, ich sprostowania, usunięcia lub ograniczenia przetwarzania oraz o prawo wniesienia
                  sprzeciwu wobec ich przetwarzania. Zostałam/em poinformowana/y o przysługującym mi prawie do cofnięcia
                  zgody, w dowolnym momencie, w formie, w jakiej została ona wyrażona. Wycofanie zgody nie wpływa na
                  zgodność z prawem przetwarzania, którego dokonano na podstawie zgody przed jej wycofaniem.
                </Trans>
              }
              type={CONTROL_TYPE.CHECKBOX}
            />
          )}
        />
        <Controller
          control={control}
          name="application.agreementVsibleForEmployer"
          render={({ field: { name: fieldName, onChange, value } }) => (
            <Control
              onChange={onChange}
              value={value}
              name={fieldName}
              checked={value}
              error={errors.application?.agreementVsibleForEmployer}
              label={
                <Trans i18nKey="apply.agreementVsibleForEmployer" t={offersTrans}>
                  Na podstawie art. 6 ust. 1 lit a) rozporządzenia Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia
                  27 kwietnia 2016 r. w sprawie ochrony osób fizycznych w związku z przetwarzaniem danych osobowych i w
                  sprawie swobodnego przepływu takich danych oraz uchylenia dyrektywy 95/46/WE (ogólnego rozporządzenia
                  o ochronie danych), Dz.U.UE.L.2016.119.1, oświadczam, iż wyrażam zgodę na udostępnienie moich danych
                  osobowych zarejestrowanym w Serwisie Pracodawcom w celu aplikowania na dostępne oferty staży
                  wakacyjnych w ramach projektu „Praktykuj w Łodzi-staże wakacyjne”. Powyższa zgoda oznacza, że została
                  wyrażona dobrowolnie, świadomie i stanowi jednoznaczne okazanie mojej woli.
                </Trans>
              }
              type={CONTROL_TYPE.CHECKBOX}
            />
          )}
        />

        <Controller
          control={control}
          name="application.agreementRodo"
          render={({ field: { name: fieldName, onChange, value } }) => (
            <Control
              onChange={onChange}
              value={value}
              name={fieldName}
              checked={value}
              error={errors.application?.agreementRodo}
              label={
                <Trans i18nKey="apply.agreementRodo" t={offersTrans}>
                  Oświadczam, że zapoznałem się i akceptuję Klauzulę Informacyjną
                  <a href="https://praktyki.lodz.pl/klauzula-informacyjna-rodo" target="_blank" rel="noreferrer">
                    RODO
                  </a>
                  oraz
                  <a
                    href="https://www.profi.lodz.pl/regulamin-serwisu-www-praktyki-lodz-pl"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Regulamin serwisu
                  </a>
                  „profi.lodz.pl”.
                </Trans>
              }
              type={CONTROL_TYPE.CHECKBOX}
            />
          )}
        />
      </Agreements>

      <Button type={BUTTON_TYPE.SUBMIT}>{offersTrans('Send application')}</Button>
    </Form>
  );
}

export default ApplyModal;
